import { mapEntity } from '@api';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ColumnDef } from '@tanstack/react-table';
import { OneDayReturnCell } from '@terminal/components/one-day-return-cell/OneDayReturnCell';
import { CompanyTableRow } from '@terminal/views/theme-overview/types';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './CompaniesTable.module.scss';
import { CompanyNameCell } from './company-name-cell/CompanyNameCell';

export const THEME_OVERVIEW_RANK_COLUMN_ID = 'rank';
export const THEME_OVERVIEW_COMPANY_COLUMN_ID = 'company';
export const THEME_OVERVIEW_SECTOR_COLUMN_ID = 'sector';
export const THEME_OVERVIEW_DESCRIPTION_COLUMN_ID = 'description';
export const THEME_OVERVIEW_1D_RETURN_COLUMN_ID = 'return1D';

export const useColumns = () => {
  const { t } = useTranslation(['widget', 'common']);

  return useMemo<ColumnDef<CompanyTableRow>[]>(
    () => [
      {
        id: THEME_OVERVIEW_RANK_COLUMN_ID,
        header: t('widget:themeOverview.companiesRank'),
        accessorKey: 'rank',
        cell: props => {
          const { original } = props.row;
          return (
            <div className={styles.rankWrapper}>
              <TopRankIcon rank={original.relationship.rank} />
            </div>
          );
        },
      },
      {
        id: THEME_OVERVIEW_COMPANY_COLUMN_ID,
        header: t('widget:themeOverview.company'),
        accessorKey: 'company',
        cell: props => {
          if (props.row.original.isFetching) {
            return (
              <SkeletonLoader
                areas={[
                  {
                    styles: {
                      width: '100%',
                      height: '20px',
                      borderRadius: '8px',
                    },
                  },
                ]}
              />
            );
          }

          return <CompanyNameCell entity={props.row.original} />;
        },
      },
      {
        id: THEME_OVERVIEW_SECTOR_COLUMN_ID,
        header: t('widget:themeOverview.sector'),
        accessorKey: 'sector',
        cell: props => {
          if (props.row.original.isFetching) {
            return (
              <SkeletonLoader
                areas={[
                  {
                    styles: {
                      width: '100%',
                      height: '20px',
                      borderRadius: '8px',
                    },
                  },
                ]}
              />
            );
          }

          if (!props.row.original.gics) {
            return (
              <span
                className={styles.sectorNa}
                data-testid="table-cell-empty-value"
              >
                {t('common:table.na')}
              </span>
            );
          }

          const sectorName = t([
            `widget:assetOverview.companyProfile.sectorNames.${props.row.original.gics.sector.code}`,
            'widget:assetOverview.companyProfile.sectorNames.notFound',
          ]);

          return <div className={styles.sectorName}>{sectorName}</div>;
        },
      },
      {
        id: THEME_OVERVIEW_DESCRIPTION_COLUMN_ID,
        header: t('widget:themeOverview.description'),
        accessorKey: 'description',
        cell: props => (
          <div className={styles.descriptionWrapper}>
            {props.row.original.relationship.description}
          </div>
        ),
      },
      {
        id: THEME_OVERVIEW_1D_RETURN_COLUMN_ID,
        header: t('widget:themeOverview.return1D'),
        accessorKey: 'return1D',
        cell: props => {
          if (props.row.original.isFetching) {
            return (
              <SkeletonLoader
                areas={[
                  {
                    styles: {
                      width: '100%',
                      height: '20px',
                      borderRadius: '8px',
                    },
                  },
                ]}
              />
            );
          }

          return (
            <OneDayReturnCell
              asset={mapEntity(props.row.original)}
              showLiveIndicator
            />
          );
        },
      },
    ],
    [t]
  );
};

export const getAlignment = (index: number) => {
  return index > 3 ? 'end' : 'start';
};
